(function(angular, _) {
    'use strict';

    /**
     * @todo Moved into a component FS structure. Full code refactoring required.
     */

    angular.module('wtApp.wtFilterEngine').service('activateGroupedFilterService', activateGroupedFilterService);

    activateGroupedFilterService.$inject = ['WT_CONST', 'GFL_CONST', 'configService', 'Monads', 'logicalCubeUtils', 'metricService', 'segmentService', 'LiveFilters', '$injector'];

    function activateGroupedFilterService(WT_CONST, GFL_CONST, configService, Monads, logicalCubeUtils, metricService, segmentService, LiveFilters, $injector) {
        const filterUtils = $injector.get('filterUtils');

        /**
         * Will inject the 'fake' segment dimension to a hash of dimension - ONLY for RAW_DATA layer.
         * Filter engine on REAL_TIME layer should not include segments
         * @param  {Object} dimensions Hash map of dimensions
         * @param  {string} layer
         * @private
         */
        const injectSegmentDimension_ = (dimensions, layer) => {
            // Does nothing if on LA layer
            if (LiveFilters.isLive(layer)) {
                return;
            }

            const $translate = $injector.get('$translate');

            const segmentDimension = segmentService
                                        .getSegmentAsDimensionTemplate(
                                            $translate.instant('SEGMENTS_TITLE'),
                                            'VISITORS',
                                            $translate.instant('SEGMENTS_DROPDOWN_DEFAULT_VALUE')
                                        );

            _.set(segmentDimension, 'sortMapping', true);
            _.set(dimensions, segmentDimension.arrangeType, segmentDimension);
        };

        /**
         * Will filter dimensions for RAW data layer. Raw filter rules must share a common logical cube with
         * at least one intersection combination of all dimensions and one metric at a time.
         * @param   {Array<Object>} columns - List of columns configurations for a given analysis
         * @returns {Array<Object>} Filtered list of dimension configurations
         * @private
         */
        this.getRawFilteredDimensions_ = function getRawFilteredDimensions_(columns) {
            const layer = Monads.RAW_DATA;
            // Get the list of grouped logical cube sets by dimensions and metric(s)
            const logicalCubeList = logicalCubeUtils.getListOfIntersectedLogicalCubeCombinationsFromColumns(columns, false, layer);
            const dimensions = _.chain(Monads.layer(layer).dimensions())
                                .cloneDeep()
                                .omitBy((dimension) => {
                                    if (_.includes(WT_CONST.FILTER.REJECTED_ARRANGE_TYPES, dimension.arrangeType)) {
                                        // Do not show rejected arrangeTypes
                                        return true;
                                    } else if (_.includes(GFL_CONST.TIME_FILTER_ARRANGE_TYPES, dimension.arrangeType)) {
                                        // Allow time dimensions
                                        return false;
                                    } else if (dimension.internalObject) {
                                        // Reject internalObjects
                                        return true;
                                    } else if (!logicalCubeUtils.isCompatibleLogicalCubeList(logicalCubeList, dimension.logicalCubes)) {
                                        // Reject non matching by logical cubes
                                        return true;
                                    }
                                        // Other then that it's ok
                                    return false;
                                })
                                .value();
            injectSegmentDimension_(dimensions, layer);
            return dimensions;
        };

        /**
         * Will filter dimensions for REAL_TIME data layer. Real time filter rules must share a common logical cube with
         * the aggregate intersection - i.e. the logical cube intersection of ALL analysis dimensions and metrics.
         * @param   {string} analysisId - The analysis id on which the filters are to be applied
         * @returns {Array<Object>} Filtered list of dimension configurations
         * @private
         */
        this.getLiveFilteredDimensions_ = function getLiveFilteredDimensions_(analysisId) {
            const layer = Monads.REAL_TIME;
            // Get the list of grouped logical cube sets by dimensions and metric(s)
            const aggregates = this.getAggregates(analysisId);
            const dimensions = _.chain(Monads.layer(layer).dimensions())
                                .cloneDeep()
                                .omitBy((dimension) => {
                                    if (_.includes(WT_CONST.FILTER.REJECTED_ARRANGE_TYPES, dimension.arrangeType)) {
                                        // Do not show rejected arrangeTypes
                                        return true;
                                    } else if (_.includes(GFL_CONST.TIME_FILTER_ARRANGE_TYPES, dimension.arrangeType)) {
                                        // Allow time dimensions
                                        return false;
                                    } else if (dimension.internalObject) {
                                        // Reject internalObjects
                                        return true;
                                    } else if (_.isEmpty(logicalCubeUtils.getIntersectedLogicalCubes(aggregates, dimension.logicalCubes))) {
                                        // Reject non matching by logical cubes / aggregates
                                        return true;
                                    }
                                        // Other then that it's ok
                                    return false;
                                })
                                .value();
            return dimensions;
        };

        /**
         * Will return a filtered list of dimension configurations that are usable in the filter engine based on general and
         * analysis specific criteria.
         * @param   {string} analysisId - The analysis id on which the filters are to be applied
         * @returns {Array<Object>} Filtered list of dimension configurations
         */
        this.filterDimensionsByColumns = function filterDimensionsByColumns(analysisId) {
            const analysis = configService.getAnalysis(analysisId);
            const layer = analysis.getLayer();
            // Differentiate filtering logic based on layer
            if (LiveFilters.isLive(layer)) {
                return this.getLiveFilteredDimensions_(analysisId);
            }

            return this.getRawFilteredDimensions_(analysis.getViewModeColumns(), analysis);
        };

        /**
         * Will return a filtered list of metric configurations that are usable in the filter engine based on general and
         * analysis specific criteria.
         * @param   {string} analysisId - The analysis id on which the filters are to be applied
         * @returns {Array<Object>} Filtered list of metric configurations
         */
        this.filterMetricsByColumns = function filterMetricsByColumns(analysisId) {
            const analysis = configService.getAnalysis(analysisId);
            const layer = analysis.getLayer();
            // No metric filters for live analyses
            if (LiveFilters.isLive(layer)) {
                return [];
            }

            // Get the list of grouped logical cube sets by dimensions and metric(s)
            const logicalCubeList = logicalCubeUtils
                .getListOfIntersectedLogicalCubeCombinationsFromColumns(analysis.getViewModeColumns(), false, layer);
            return _.chain(Monads.layer(layer).metrics())
                    .cloneDeep()
                    .omitBy(function(metric) {
                        if (!metric) {
                            return true;
                        }

                        // Excludes arrangeTypes from the list of 'rejectables'
                        if (_.includes(WT_CONST.FILTER.REJECTED_ARRANGE_TYPES, metric.arrangeType)) {
                            return true;
                        }

                        // Excludes internalObjects
                        if (metric.internalObject) {
                            return true;
                        }

                        // Excludes all formulas; condition covers both custom(er) and predefined formulas
                        if (_.hasIn(metric, 'customFormula')) {
                            return true;
                        }

                        // Exclude custom metrics. Should always have baseArrangeType property set
                        if (_.hasIn(metric, 'baseArrangeType')) {
                            return true;
                        }

                        // Allow context NONE metrics - not Google Adwords metrics
                        if (metricService.ignoresLogicalCubes(metric)) {
                            return false;
                        }

                        // Reject non matching by logical cubes
                        if (!logicalCubeUtils.isCompatibleLogicalCubeList(logicalCubeList, metric.logicalCubes)) {
                            return true;
                        }

                        return false;
                    })
                    .value();
        };

        /**
         * Will compare the filter list of the last request of an analysis and a given list of filters. Returns true
         * if filter list of query and given filter list differ; false otherwise.
         * @param  {Object}  iLastQuery - The calculation (a.k.a. query) object of a report generation request
         * @param  {Array<Object>}  iFilters - A list of filter rules
         * @returns {boolean}
         */
        this.hasChangedFilters_ = function hasChangedFilters_(iLastQuery, iFilters) {
            const ignoreProps = ['sequential', 'id', 'internalObject'];
            return !$injector
                        .get('utils')
                        .compareObjectLists(_.get(iLastQuery, 'filters'), iFilters, ignoreProps);
        };

        /**
         * Will compare the list of containers of the last request of an analysis and a given list of containers.
         * Returns true if the lists differ; false otherwise.
         * @param  {Object}  iLastQuery - The calculation (a.k.a. query) object of a report generation request
         * @param  {Array<Object>}  iContainers - A list of container objects
         * @returns {boolean}
         */
        this.hasChangedContainers_ = function hasChangedContainers_(iLastQuery, iContainers) {
            const ignoreProps = ['sequential', 'id', 'internalObject', 'intern'];
            return !filterUtils.compareContainers(_.get(iLastQuery, 'containers'), iContainers, ignoreProps);
        };

        /**
         * Will compare filters and containers with the given containers and filters.
         * @param  {string}  iAnalysisId
         * @param  {Array<Object>}  iFilters - A list of filter rules
         * @param  {Array<Object>}  iContainers - A list of container objects
         * @returns {boolean}
         */
        this.hasChangedTimeSeriesQuery_ = function hasChangedTimeSeriesQuery_(iAnalysisId, iFilters, iContainers) {
            const analysis = configService.getAnalysis(iAnalysisId);

            if (!_.chain(analysis).get('isTimeSeriesViewMode').isFunction().value()) {
                return false;
            }

            // Nothing to do if the analysis is not in time series view - NOT edit - mode
            if (!analysis.isTimeSeriesViewMode() || analysis.getEditState()) {
                return false;
            }

            // Get latest query, with time filter.
            const query = analysis.getPredefinedQuery(true, false);

            if (this.hasChangedFilters_(query, iFilters)) {
                return true;
            }

            if (this.hasChangedContainers_(query, iContainers)) {
                return true;
            }

            return false;
        };

        /**
         * Will determine if the filter engine has changed the filters and containers for the current analysis
         * @param  {string}  iAnalysisId
         * @param  {Array<Object>}  iFilters - A list of filter rules
         * @param  {Array<Object>}  iContainers - A list of container objects
         * @returns {boolean}
         */
        this.hasChangedQuery = function hasChanged(iAnalysisId, iFilters, iContainers) {
            const iLastQuery = this.getLatestQuery(iAnalysisId);

            // Filters might have changed
            if (this.hasChangedFilters_(iLastQuery, iFilters)) {
                return true;
            }

            if (this.hasChangedContainers_(iLastQuery, iContainers)) {
                return true;
            }

            if (this.hasChangedTimeSeriesQuery_(iAnalysisId, iFilters, iContainers)) {
                return true;
            }

            return false;
        };

        /**
         * Will add a time filter to a filter list if it has not default time filter already
         * @param {string} analysisId
         * @param {Array<Object>} filterList - List of filter rules
         */
        this.addTimeFilter = function addTimeFilter(analysisId, filterList) {
            const Filters = $injector.get('Filters');
            // Has already time. So do nothing
            if (Filters.hasTimeFilter(filterList)) {
                Filters.afterTime(filterList);
                return this;
            }

            const analysis = configService.getAnalysis(analysisId);
            const timeFilter = _.chain(analysis.getSessionTimeFilter())
                                .set('internalObject', true)
                                .value();

            Filters.insertTimes(filterList, timeFilter);
            Filters.afterTime(filterList);
            return this;
        };

        /**
         * Will fetch a copy of the latest filters and containers for an analysis. Either
         * from the query cache or will construct a query object from the analysis object
         * @param  {string} iAnalysisId
         * @returns {Object}
         */
        this.getLatestQuery = function getLatestQuery(iAnalysisId) {
            const analysis = configService.getAnalysis(iAnalysisId);

            const query = analysis.getPredefinedQuery(true);

            const filters = filterUtils.omitFilterProperties(query.filters, ['numberOfDays']);
            let containers = filterUtils.normalizeContainer(query);
            containers = filterUtils.omitFilterProperties(containers, ['numberOfDays']);

            return {filters, containers};
        };

        /**
         * Will try to spot the position of a segment basewd on the list of segment
         * connections persisted in the analysis configuration
         * @param   {string} analysisId
         * @param   {Object} segment - segment connection configuration object
         * @returns {number|null}
         * @private
         */
        this.getSegmentPosition_ = function getSegmentPosition_(analysisId, segment) {
            // If the segment option already includes a position return it...
            if (_.chain(segment).get('position').isNumber().value()) {
                return _.get(segment, 'position');
            }
            // ...else try to fish it from the analysis
            const analysis = configService.getAnalysis(analysisId);

            if (!analysis) {
                return null;
            }

            return _.chain(analysis.get('predefinedSegmentConnections'))
                .find((predefinedSegmentConnection) => {
                    return `${predefinedSegmentConnection.segmentID}` === `${segment.segmentID}`;
                })
                .get('position')
                .value();
        };

        /**
         * Will map the segment object to a proper object. Needed as segments may come
         * from the predefinedSegmentConnections - with proper object structure - or could
         * come as segment constructed from a filter object. In this case the segment related
         * properties are mapped to predefinedSegmentConnections object properties and the
         * reconstructed object is returned.
         * @param   {Object} segment
         * @returns {Object}
         * @private
         */
        this.mapContainerToSegment_ = function mapContainerToSegment_(segment) {
            const PROP_MAP = {
                segmentId: 'segmentID',
                context: 'context',
                connector: 'connectorPredicate',
                inOrNotIn: 'inOrNotInPredicate',
                position: 'position'
            };

            const PROPS = _.values(PROP_MAP);

            return _.chain(segment)
                .mapKeys((value, key) => {
                    // If already one of the proper keys, no need to map
                    if (_.includes(PROPS, key)) {
                        return key;
                    }
                    // Try to map the key
                    return _.get(PROP_MAP, `${key}`);
                })
                .pick(PROPS)
                .value();
        };

        /**
         * Will get the predefined segments - the predefinedSegmentConnections list - of
         * an analysis ONLY if it was loaded for the first time. Will return null otherwise.
         * @param   {string} analysisId
         * @returns {Array<Object>|null}
         */
        this.getPredefinedSegments_ = function getPredefinedSegments_(analysisId) {
            const $rootScope = $injector.get('$rootScope');

            const initLoad = _.get($rootScope, `INIT_ANALYSIS_LOAD.${analysisId}`);
            // If the analysis has already been loaded once ignore the
            // predefined segments, hence return null
            if (!initLoad) {
                return null;
            }

            const analysis = configService.getAnalysis(analysisId);

            if (!analysis) {
                return null;
            }

            const predefinedSegmentConnections = analysis.get('predefinedSegmentConnections');

            return _.isArray(predefinedSegmentConnections) ? predefinedSegmentConnections : null;
        };

        /**
         * Will return the segments based on analysis id; either the predefined or
         * the collected from the containers
         * @param   {string} analysisId
         * @param   {Array<Object>} segments
         * @returns {Array<Object>}
         * @private
         */
        this.getSegments_ = function getSegments_(analysisId, segments) {
            const defaultSegments = this.getPredefinedSegments_(analysisId);

            if (defaultSegments) {
                return defaultSegments;
            }

            if (_.isArray(segments)) {
                return segments;
            }

            return [];
        };

        /**
         * Will convert a list of segments - either collected from filter engine containers
         * or from predefinedSegmentConnections to a list of filters to be injected to the filter list
         * that is to be utilized from the filter engine
         * @param   {string} analysisId
         * @param   {Array<Object>} segments_  [description]
         * @returns {Array<Object>}
         * @private
         */
        this.getSegmentFilterList_ = function getSegmentFilterList_(analysisId, segments_) {
            const segments = this.getSegments_(analysisId, segments_);
            return _.chain(segments)
                .map((segment) => {
                    return this.mapContainerToSegment_(segment);
                })
                .filter((segmentConnector) => {
                    const segmentID = _.get(segmentConnector, 'segmentID');

                    if (!_.isNumber(segmentID)) {
                        return false;
                    }

                    const segment = segmentService.getActiveSegmentById(segmentID);
                    return !_.isEmpty(segment);
                })
                .map((segment) => {
                    const position = this.getSegmentPosition_(analysisId, segment);
                    const filter = segmentService.getSegmentConnectorAsFilter(segment, false);
                    _.set(filter, 'position', position || 0);
                    return filter;
                })
                .value();
        };

        /**
         * Will trace a converted segment filter in a list of filters.
         * @param   {string|number} segmentId
         * @param   {Array<Object>} filters
         * @returns {Object|undefined}
         * @private
         */
        this.getSegmentFromFilters_ = function getSegmentFromFilters_(segmentId, filters) {
            const SEGMENTS_ARRANGETYPE = segmentService.getFakeSegmentArrangeType();
            return _.find(filters, (filter) => {
                if (_.get(filter, 'arrangeType') !== SEGMENTS_ARRANGETYPE) {
                    return false;
                }
                return _.get(filter, 'value1') === `${segmentId}`;
            });
        };

        /**
         * Will convert a list of segments to segment filters and inject them into a given list
         * of filters, avoiding duplicates.
         * @param  {string} analysisId
         * @param  {Array<Object>} filters
         * @param  {Array<Object>} segments
         * @chainable
         */
        this.injectSegments = function injectSegments(analysisId, filters, segments) {
            // The segments as filters, ready to be injected in the filters list
            const segmentFilters = this.getSegmentFilterList_(analysisId, segments);

            _.forEach(segmentFilters, (segmentFilter) => {
                const inFilters = this.getSegmentFromFilters_(segmentFilter.value1, filters);

                if (inFilters) {
                    _.extend(inFilters, segmentFilter);
                    return;
                }

                this.addIds(segmentFilter);

                filters.push(segmentFilter);
            });

            return this;
        };

        /**
         * Will remove the mapping property from a list or single filter
         * @param  {Array<Object>|Object} filters_
         * @chainable
         */
        this.removeMapping = function removeMapping(filters_) {
            const filters = _.isArray(filters_) ? filters_ : [filters_];
            _.forEach(filters, (filter) => {
                delete filter.mapping;
            });
            return this;
        };

        /**
         * Will replace the contents of a given list with the contents of another list
         * without breaking the reference.
         * @param  {Array<*>} list
         * @param  {Array<*>} altList
         * @chainable
         */
        this.replaceList_ = function replaceList_(list, altList) {
            _.remove(list, () => {
                return true;
            });
            _.forEach(altList, (item) => {
                list.push(item);
            });
            return this;
        };

        /**
         * Will eliminate duplicate elements from a list without breaking the reference.
         * @param  {Array<Object>} filters
         * @chainable
         */
        this.removeDuplicates = function removeDuplicates(filters) {
            // Collect unique filters
            const uniqueFilters = _.uniqBy((filters), (filter) => {
                return JSON.stringify(filter);
            });
            this.replaceList_(filters, uniqueFilters);
            return this;
        };

        /**
         * Will sort a list of filters based on their position property
         * @param  {Array<Object>} filters
         * @chainable
         */
        this.sortByPosition = function sortByPosition(filters) {
            const sortedFilters = _.sortBy(filters, (filter) => {
                return _.get(filter, 'position');
            });
            this.replaceList_(filters, sortedFilters);
            return this;
        };

        /**
         * Will extract the time filters from a list of filters, select the first one and
         * put it on top of the given filter list.
         * @param  {Array<Object>} filters
         * @chainable
         */
        this.timeFilterFirst = function timeFilterFirst(filters) {
            const Filters = $injector.get('Filters');
            const timeFilters = _.chain(filters)
                .remove((filter) => {
                    return Filters.isTime(filter);
                })
                .map((filter, index) => {
                    // Keep only the first time filter
                    if (index) {
                        return null;
                    }
                    // Set the internalObject property
                    _.set(filter, 'internalObject', true);
                    return filter;
                })
                .compact()
                .value();
            this.replaceList_(filters, timeFilters.concat(filters));
            return this;
        };

        /**
         * Will go over a list of filters an add random ids to every one of them. Works
         * for single ites too.
         * @param  {Array<Object>|Object} filters_
         * @chainable
         */
        this.addIds = function addIds(filters_) {
            const filters = _.isArray(filters_) ? filters_ : [filters_];
            filterUtils.addRandomIdsToFilterList(filters);
            return this;
        };

        /**
         * Will set the sequential property to true on containers with type 'TIME_ORDER'.
         * @param  {Array<Object>|Object} containers_
         * @chainable
         */
        this.setSequential = function setSequential(containers_) {
            const containers = _.isArray(containers_) ? containers_ : [containers_];
            _.chain(containers)
                .filter({type: GFL_CONST.CONTAINER_SEQUENCE_INDICATOR})
                .forEach(container => {
                    _.chain(container).get('filters').forEach(filter => {
                        filter.sequential = true;
                    }).value();
                })
                .value();
            return this;
        };

        /**
         * Will return the intersection of logical cubes - a.k.a. aggregates for live analyses - of all columns.
         * @param   {string} analysisId
         * @returns {Array<string>}
         */
        this.getAggregates = function getAggregates(analysisId) {
            const analysis = configService.getAnalysis(analysisId);
            if (!analysis) {
                return [];
            }
            return logicalCubeUtils.getIntersectedLogicalCubesByAnalysis(analysis.config, analysis.layer);
        };

        /**
         * Will retrieve the monad configuration of the first dimension of the query of an analysis instance.
         * @param   {string} analysisId
         * @returns {Object}
         */
        this.getMainColumnDimension = function getMainColumnDimension(analysisId) {
            const analysis = configService.getAnalysis(analysisId);
            let arrangeType;
            if (analysis.isTimeSeriesViewState()) {
                // When in time series view state the first dimension is always a time dimension and not
                // the registered dimension column.
                arrangeType = analysis.timeSeriesTimeArrangeType;
            } else {
                arrangeType = _.get(analysis.columns.getFirstDimension(), 'arrangeType');
            }
            return Monads.layer(analysis.getLayer()).monad(arrangeType);
        };
    }
})(window.angular, window._);
